import React, {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTheme} from "../../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../../connectui/component/buildStyles";
import Text from "../../../../../connectui/component/display/Text";
import DropdownExpand from "../../../../../connectui/component/display/dropdown/DropdownExpand";
import FlexContainer from "../../../../../connectui/component/layout/FlexContainer";
import ICONS from "connectui/icons/iconMap";
import Divider from "../../../../../connectui/component/display/Divider";
import Container from "../../../../../connectui/component/layout/Container";
import DropDownSelect from "../../../../../connectui/component/input/dropdown/DropDownSelect";
import {getGuestContactInfo} from "../../../../../utilities/helperFunctions";
import {
  getInboxData,
  updateGuest as updGuest,
} from "redux/actions/guestsActions";
import ChipDropDownBase from "../../../../../connectui/component/input/dropdown/base/ChipDropDownBase";
import SvgIcon from "../../../../../connectui/component/display/SvgIcon";
import GuestDetailsTags from "../../guestdetails/guest/GuestDetailsTags";
import EditableFieldValue from "../../../../../connectui/component/layout/field/EditableFieldValue";
import PhoneInput from "connectui/component/input/text/type/PhoneInput";
import GuestDetailsFollowers from "../../guestdetails/guest/GuestDetailsFollowers";
import LanguageSelector from "../../../../../connectui/component/input/dropdown/type/LanguageSelector";
import {useTranslation} from "react-i18next";
import {format} from "date-fns";
import SentimentField from "../../../field/fields/SentimentField";
import {satisfaction} from "../../../../../configuration/constants";
import ModalBase from "../../../../../connectui/component/modal/ModalBase";
import ButtonText from "../../../../../connectui/component/input/button/ButtonText";
import ChipText from "../../../../../connectui/component/display/chip/ChipText";
import ImageCarousel from "../../../../../connectui/component/display/carousel/ImageCarousel";
import RoundIcon from "../../../../../connectui/component/display/RoundIcon";
import OutlinedButtonBase from "../../../../../connectui/component/input/button/base/OutlinedButtonBase";

const useStyles = (theme) =>
  buildStyles({
    fieldContainer: {
      paddingTop: theme.spacing(2),
    },
    userImageContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  });

export default function GuestSection({guestId, fullDetails = false}) {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const [guest, setGuest] = React.useState(guests[guestId]);
  const [assignedTo, setAssignedTo] = React.useState(null);
  const [expanded, setExpanded] = React.useState(true);
  const [phone, setPhone] = React.useState("");

  const [isEditing, setIsEditing] = React.useState({
    email: false,
    phone: false,
    name: false,
  });
  const [phoneError, setPhoneError] = React.useState(false);
  const [isPhoneValid, setIsPhoneValid] = React.useState(true);
  const [idModalOpen, setIdModalOpen] = React.useState(false);
  const [selectedIdType, setSelectedIdType] = React.useState(null);
  const {t} = useTranslation();
  const users = React.useMemo(() => {
    if (!user_profile?.enso_users) {
      return [];
    }
    return user_profile.enso_users.map((u) => ({
      ...u,
      picture: u.picture ?? u.profile_picture,
    }));
  }, [user_profile]);

  const assignee = React.useMemo(() => {
    const euId = assignedTo?.enso_user_id || guest?.assigned_to || "";
    return users.find((u) => u.enso_user_id === euId) ?? null;
  }, [assignedTo, users, guest]);

  const {guest_email, guest_phone} = React.useMemo(
    () => getGuestContactInfo(guest),
    [guest],
  );

  const idDocs = React.useMemo(() => {
    if (!guest?.metadata?.identity_docs?.length) {
      return [];
    }
    return guest.metadata.identity_docs;
  }, [guest?.metadata?.identity_docs]);

  const frontIdDoc = React.useMemo(() => {
    return idDocs.findLast(
      (doc) =>
        doc.document_type.includes("Front") ||
        doc.document_type.includes("govt_id_front"),
    );
  }, [idDocs]);

  const backIdDoc = React.useMemo(() => {
    return idDocs.findLast(
      (doc) =>
        doc.document_type.includes("Back") ||
        doc.document_type.includes("govt_id_back"),
    );
  }, [idDocs]);

  const selfieDoc = React.useMemo(() => {
    return idDocs.findLast(
      (doc) =>
        doc.document_type.includes("Selfie") ||
        doc.document_type.includes("selfie"),
    );
  }, [idDocs]);

  const carouselImages = React.useMemo(() => {
    const images = [];

    if (frontIdDoc?.url) {
      images.push({
        url: frontIdDoc.url,
        alt: "Front ID",
        type: "front",
      });
    }

    if (backIdDoc?.url) {
      images.push({
        url: backIdDoc.url,
        alt: "Back ID",
        type: "back",
      });
    }

    if (selfieDoc?.url) {
      images.push({
        url: selfieDoc.url,
        alt: "Selfie",
        type: "selfie",
      });
    }

    return images;
  }, [frontIdDoc, backIdDoc, selfieDoc]);

  const initialCarouselIndex = React.useMemo(() => {
    if (!selectedIdType || !carouselImages.length) return 0;

    const index = carouselImages.findIndex(
      (img) => img.type === selectedIdType,
    );
    return index >= 0 ? index : 0;
  }, [selectedIdType, carouselImages]);

  const availableDocs = React.useMemo(() => {
    const docs = [];
    if (frontIdDoc) docs.push({type: "front", doc: frontIdDoc});
    if (backIdDoc) docs.push({type: "back", doc: backIdDoc});
    if (selfieDoc) docs.push({type: "selfie", doc: selfieDoc});
    return docs;
  }, [frontIdDoc, backIdDoc, selfieDoc]);

  useEffect(() => setPhone(guest_phone), [guest_phone]);
  useEffect(() => setGuest(guests[guestId]), [guests, guestId]);
  useEffect(
    () => setIsEditing({email: false, phone: false, name: false}),
    [guestId],
  );
  useEffect(() => {
    setAssignedTo(
      users.find((u) => u.enso_user_id === guests[guestId]?.assigned_to),
    );
  }, [guests, guestId, users]);

  const updateGuest = React.useCallback(
    (field, val, update) => {
      update &&
        dispatch(
          updGuest({
            guest_id: guest.guest_id,
            field,
            val,
            onSuccess: () => {
              dispatch(getInboxData());
            },
          }),
        );
    },
    [dispatch, guest?.guest_id],
  );

  const handleAssigneeChange = (newAssigneeId) => {
    const newAssignee = users.find((u) => u.enso_user_id === newAssigneeId);

    if (newAssignee) {
      setAssignedTo(newAssignee);
      updateGuest("assigned_to", newAssigneeId, true);
    } else {
      setAssignedTo(null);
      updateGuest("assigned_to", null, true);
    }
  };

  const handleLanguageUpdate = (newLang) => {
    updateGuest("language", newLang, true);
  };

  const handleSentimentChange = (newSelection) => {
    updateGuest("rating", satisfaction[newSelection].value, true);
  };

  const handleNameChange = (newSelection) => {
    updateGuest("name", newSelection, true);
  };

  const openIdModal = (idType) => {
    setSelectedIdType(idType);
    setIdModalOpen(true);
  };

  const closeIdModal = () => {
    setIdModalOpen(false);
    setSelectedIdType(null);
  };

  const IdModal = () => {
    return (
      <ModalBase isOpen={idModalOpen} onClose={closeIdModal}>
        <FlexContainer
          flexDirection="column"
          alignItems="flex-start"
          gap={theme.spacing(2)}
          sx={{
            padding: theme.spacing(2, 3),
            minWidth: "500px",
            maxWidth: "1500px",
            height: "70vh",
          }}
        >
          <RoundIcon
            iconComponent="id-card"
            baseColor="primary"
            sx={{margin: theme.spacing(-1, 0, 0, -1)}}
          />
          <FlexContainer
            flexDirection="column"
            gap={theme.spacing(1)}
            sx={{width: "100%"}}
          >
            <Text variant="textLG" weight="semibold">
              ID Documents
            </Text>
            <Text variant="textSM" color="gray600">
              View identification documents for this guest
            </Text>
          </FlexContainer>

          <Container
            sx={{
              minHeight: "400px",
              minWidth: "500px",
              maxHeight: "100%",
              maxWidth: "100%",
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(1),
            }}
          >
            <ImageCarousel
              images={carouselImages}
              initialIndex={initialCarouselIndex}
              showControls={carouselImages.length > 1}
            />
          </Container>

          <FlexContainer
            gap={theme.spacing(3)}
            justifyContent="center"
            sx={{width: "100%", marginTop: theme.spacing(1)}}
          >
            <ButtonText
              buttonBase={OutlinedButtonBase}
              label="Close"
              onClick={closeIdModal}
              sx={{minWidth: "120px"}}
            />
          </FlexContainer>
        </FlexContainer>
      </ModalBase>
    );
  };

  function getField(label, value, icon) {
    return (
      <FlexContainer
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        gap={theme.spacing(3)}
      >
        <FlexContainer
          flexDirection="row"
          alignItems="center"
          gap={theme.spacing(1)}
        >
          <SvgIcon
            component={ICONS[icon]}
            sx={{
              color: theme.colors.textSecondary,
              width: "14px",
              height: "14px",
            }}
          />
          <Text variant={"textSM"} color={"textSecondary"} weight="medium">
            {label}
          </Text>
        </FlexContainer>
        <FlexContainer justifyContent="flex-end" sx={{maxWidth: "80%"}}>
          {value}
        </FlexContainer>
      </FlexContainer>
    );
  }

  return (
    <DropdownExpand
      divider={false}
      expanded={expanded}
      onExpand={() => setExpanded(!expanded)}
      customLabel={
        <FlexContainer alignItems="center" gap={theme.spacing(2)}>
          <Text variant={"textLG"} weight="semibold">
            {t("inbox.guest-details.details.header")}
          </Text>
        </FlexContainer>
      }
    >
      <FlexContainer
        className={classes.fieldContainer}
        alignItems={"flex-start"}
        justifyContent={"flex-start"}
        gap={theme.spacing(4)}
        flexDirection={"column"}
      >
        {fullDetails &&
          getField(
            t("inbox.guest-details.details.guest-name"),
            <EditableFieldValue
              value={guest?.name}
              controlledEdit={isEditing.name}
              onEditChange={(isEditing) =>
                setIsEditing((prev) => ({...prev, name: isEditing}))
              }
              emptyText={t("inbox.guest-details.details.no-name")}
              onChange={(newName) => handleNameChange(newName)}
            />,
            "id-card",
          )}
        {fullDetails &&
          getField(
            t("inbox.guest-details.details.sentiment"),
            guest?.rating ? (
              <SentimentField
                type="input"
                variant="dropdown"
                fieldData={{val: guest?.rating}}
                handleValueChange={handleSentimentChange}
              />
            ) : (
              <Text variant="textXS" weight="medium" color="textTertiary">
                {t("inbox.guest-details.details.no-sentiment")}
              </Text>
            ),
            "highly-satisfied",
          )}

        {getField(
          t("inbox.guest-details.details.assignee"),
          <DropDownSelect
            dropDownBase={ChipDropDownBase}
            search
            customLabel={
              <FlexContainer alignItems="center" gap={theme.spacing(1.5)}>
                <Container className={classes.userImageContainer}>
                  <SvgIcon
                    component={ICONS.user}
                    sx={{
                      backgroundColor: theme.colors.gray50,
                      borderRadius: "50%",
                      width: "16px",
                      height: "16px",
                      padding: theme.spacing(0.5),
                    }}
                  />
                </Container>
                <Text variant="textXS" weight="medium" color="gray700">
                  {assignee?.name ||
                    t("inbox.guest-details.details.unassigned")}
                </Text>
              </FlexContainer>
            }
            sx={{content: {minWidth: "200px"}}}
            onChange={(newValue) => handleAssigneeChange(newValue)}
          >
            <FlexContainer
              key={null}
              value={null}
              label={t("inbox.guest-details.details.unassign")}
              alignItems="center"
              gap={theme.spacing(2)}
              sx={{cursor: "pointer"}}
            >
              <Container className={classes.userImageContainer}>
                <SvgIcon
                  component={ICONS.user}
                  sx={{
                    width: "18px",
                    height: "18px",
                    padding: theme.spacing(0.5),
                    color: theme.colors.textSecondary,
                  }}
                />
              </Container>
              <Text variant="textMD" weight="medium">
                {t("inbox.guest-details.details.unassign")}
              </Text>
            </FlexContainer>
            {users.map((user) => (
              <FlexContainer
                key={user.enso_user_id}
                value={user.enso_user_id}
                label={user.name}
                alignItems="center"
                gap={theme.spacing(2)}
                sx={{cursor: "pointer"}}
              >
                <Container className={classes.userImageContainer}>
                  <SvgIcon
                    component={ICONS.user}
                    sx={{
                      width: "18px",
                      height: "18px",
                      padding: theme.spacing(0.5),
                      color: theme.colors.textSecondary,
                    }}
                  />
                </Container>
                <Text variant="textMD" weight="medium">
                  {user.name}
                </Text>
              </FlexContainer>
            ))}
          </DropDownSelect>,
          "user",
        )}
        {guest?.guest_type === "guest" && (
          <GuestDetailsFollowers
            guest={guest}
            updateGuest={({guest_ids, add}) =>
              updateGuest(
                add ? "added_followers" : "removed_followers",
                guest_ids,
                true,
              )
            }
          />
        )}
        <GuestDetailsTags
          guest={guest}
          updateGuest={(val) => updateGuest("tags", val, true)}
        />
        {getField(
          t("inbox.guest-details.details.response-time"),
          guest?.response_time ? (
            <Text variant="textXS" weight="medium">
              {`${Math.round(guest?.response_time / 6) / 10} min`}
            </Text>
          ) : (
            <Text variant="textXS" weight="medium" color="textTertiary">
              {t("inbox.guest-details.details.no-time")}
            </Text>
          ),
          "clock",
        )}

        <Divider noPadding />

        {getField(
          t("inbox.guest-details.details.email"),
          <EditableFieldValue
            value={guest_email}
            controlledEdit={isEditing.email}
            onEditChange={(isEditing) =>
              setIsEditing((prev) => ({...prev, email: isEditing}))
            }
            emptyText={t("inbox.guest-details.details.no-email")}
            onChange={(e) => updateGuest("email", e, true)}
          />,
          "mail",
        )}
        {getField(
          t("inbox.guest-details.details.phone"),
          <EditableFieldValue
            emptyText={t("inbox.guest-details.details.no-phone")}
            error={phoneError && !!phone && !isPhoneValid}
            value={guest_phone}
            controlledEdit={isEditing.phone}
            onEditChange={(isEditing) =>
              setIsEditing((prev) => ({...prev, phone: isEditing}))
            }
            customInput={({value, onChange}) => (
              <>
                <PhoneInput
                  edit
                  phone={value}
                  sx={{border: "none"}}
                  onChange={(newPhone) => {
                    onChange(newPhone);
                    setPhone(newPhone);
                    setPhoneError(false);
                  }}
                  setValidPhone={setIsPhoneValid}
                />
                {phoneError && !!phone && !isPhoneValid && (
                  <Text variant="textSM" sx={{color: theme.colors.error}}>
                    {t("inbox.guest-details.details.phone-error")}
                  </Text>
                )}
              </>
            )}
            onClose={() => {
              setPhoneError(false);
              setPhone(guest_phone);
            }}
            onChange={() => {
              let newPhone = phone;
              let valid = true;
              if (newPhone) {
                valid = isPhoneValid;
                if (!newPhone.startsWith("+")) {
                  newPhone = `+${newPhone}`;
                }
              }

              if (valid) {
                if (guest_phone !== newPhone) {
                  updateGuest("phone", newPhone, true);
                }
              } else {
                setPhoneError(true);
                setIsPhoneValid(false);
              }
            }}
          />,
          "phone",
        )}
        {getField(
          t("inbox.guest-details.details.language"),
          <LanguageSelector
            handleLanguageUpdate={(update) => handleLanguageUpdate(update)}
            language={guest?.language}
          />,
          "globe",
        )}

        {guest?.metadata?.date_of_birth &&
          getField(
            t("inbox.guest-details.details.birthday"),
            <Text variant="textXS" weight="medium">
              {format(new Date(guest.metadata.date_of_birth), "MMMM d, y")}
            </Text>,
            "calendar",
          )}

        {guest?.metadata?.address &&
          getField(
            t("inbox.guest-details.details.address"),
            <Text variant="textXS" weight="medium">
              {guest.metadata.address?.formatted}
            </Text>,
            "map-pin",
          )}

        {idDocs.length > 0 &&
          getField(
            "ID Documents",
            <ChipText
              text="View ID"
              typographyVariant={"textXS"}
              onClick={() => {
                const firstDocType =
                  availableDocs.length > 0 ? availableDocs[0].type : "front";
                openIdModal(firstDocType);
              }}
            />,
            "id-card",
          )}
      </FlexContainer>
      {idModalOpen && <IdModal />}
    </DropdownExpand>
  );
}
